import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Popover,
  Typography,
  IconButton,
  Chip,
  Paper,
  Badge,
  Divider,
  Tooltip,
  Button
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useAudioNotification } from '../../hooks/useAudioNotification';

const EVENT_ICONS = {
  cd: '👥',
  vd: '🚗',
  pd: '👤',
  id: '🔒',
  vc: '🚗',
  ls: '⚠️',
  or: '🚗',
  oa: '🚗',
  ta: '⚠️',
  os: '⚠️',
  usp: '⚠️',
  ct: '📷',
  sh: '⚠️',
  ii: '💡'
};

const EVENT_LABELS = {
  cd: "Crowd Detection",
  vd: "Vehicle Detection",
  pd: "Person Detection",
  id: "Intrusion Detection",
  vc: "Traffic Congestion",
  ls: "Loading State Alert",
  or: "Off-Route Alert",
  oa: "Off-Area Alert",
  ta: "Tamper Alert",
  os: "Over Speed Alert",
  usp: "Unauthorized Stoppage",
  ct: "Camera Tampering",
  sh: "Safety Hazard",
  ii: "Insufficient Illumination"
};

const PRIORITY_COLORS = {
  High: '#f44336',
  Moderate: '#ff9800',
  Low: '#4caf50',
  normal: '#2196f3'
};

const getVehicleAlertColor = (alertType) => {
  switch (alertType) {
    case 'off-route':
      return '#4ECDC4';
    case 'off-area':
      return '#FF1744';
    case 'tamper':
      return '#E67E22';
    case 'overspeed':
      return '#E74C3C';
    case 'stoppage':
      return '#F39C12';
    default:
      return '#2196f3';
  }
};

const CompactNotificationItem = ({ notification, index, total, onClose, onClick }) => {
  const isVehicleAlert = notification.isVehicleAlert || ['or', 'oa', 'ta', 'os', 'usp'].includes(notification.eventType);
  const priorityColor = isVehicleAlert
    ? getVehicleAlertColor(notification.alertType)
    : (PRIORITY_COLORS[notification.severity] || PRIORITY_COLORS.normal);

  return (
    <Box
      onClick={() => onClick(notification)}
      sx={{
        p: 1.5,
        mb: 1,
        backgroundColor: '#243447',
        border: `1px solid ${priorityColor}33`,
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: '#2d3e52',
          borderColor: `${priorityColor}66`,
          transform: 'translateX(-2px)',
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
          {/* Icon and badge */}
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '6px',
                backgroundColor: `${priorityColor}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.1rem'
              }}
            >
              {EVENT_ICONS[notification.eventType] || '🔔'}
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#1ABC9C',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.6rem',
                fontWeight: 'bold',
                color: '#243447',
                border: '2px solid #243447'
              }}
            >
              {total - index}
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.8rem',
                mb: 0.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {EVENT_LABELS[notification.eventType] || 'New Alert'}
            </Typography>
            
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: priorityColor,
                fontSize: '0.7rem',
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 500
              }}
            >
              📍 {notification.location || 'Unknown'}
            </Typography>

            {isVehicleAlert && notification.vehicleNumber && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={notification.vehicleNumber}
                  size="small"
                  sx={{
                    height: '18px',
                    fontSize: '0.65rem',
                    backgroundColor: 'rgba(255,107,53,0.2)',
                    color: '#FF6B35'
                  }}
                />
              </Box>
            )}

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.65rem',
                mt: 0.5
              }}
            >
              {new Date(notification.timestamp).toLocaleTimeString()}
            </Typography>
          </Box>
        </Box>

        {/* Close button */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{
            color: 'rgba(255,255,255,0.5)',
            padding: '2px',
            flexShrink: 0,
            '&:hover': {
              color: '#f44336',
              backgroundColor: 'rgba(244,67,54,0.1)'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Box>
    </Box>
  );
};

const NotificationPopover = ({
  notifications = [],
  totalActiveEvents = 0,
  onNotificationClick,
  onNotificationClose
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [localNotifications, setLocalNotifications] = useState(notifications);
  const scrollContainerRef = useRef(null);
  const previousCountRef = useRef(0);

  // Audio notification hook
  const { playNotification } = useAudioNotification({
    enabled: true,
    volume: 0.7,
    soundType: 'bell',
  });

  useEffect(() => {
    setLocalNotifications(notifications);

    // Play sound when new notifications arrive
    if (notifications.length > previousCountRef.current) {
      // New notification arrived
      playNotification('bell', { volume: 0.7 });
    }

    previousCountRef.current = notifications.length;
  }, [notifications, playNotification]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClose = (notificationId) => {
    setLocalNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (onNotificationClose) {
      onNotificationClose(notificationId);
    }
  };

  const handleNotificationClick = (notification) => {
    handleNotificationClose(notification.id);
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
  };

  const handleClearAll = () => {
    localNotifications.forEach(notification => {
      if (onNotificationClose) {
        onNotificationClose(notification.id);
      }
    });
    setLocalNotifications([]);
  };

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      {/* Bell Icon Button with Sound Indicator */}
      <Tooltip title={`${localNotifications.length} unread alert${localNotifications.length !== 1 ? 's' : ''} · ${totalActiveEvents} total active`}>
        <IconButton
          onClick={handleOpen}
          sx={{
            color: localNotifications.length > 0 ? '#1ABC9C' : 'rgba(255,255,255,0.7)',
            position: 'relative',
            padding: '8px',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(26,188,156,0.1)',
              color: '#1ABC9C'
            },
            animation: localNotifications.length > 0 ? 'pulse-bell 2s infinite' : 'none',
            '@keyframes pulse-bell': {
              '0%, 100%': {
                transform: 'scale(1)'
              },
              '50%': {
                transform: 'scale(1.05)'
              }
            }
          }}
        >
          <Badge
            badgeContent={localNotifications.length}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: localNotifications.length > 0 ? '#FF1744' : 'transparent',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.7rem',
                minWidth: '18px',
                height: '18px'
              }
            }}
          >
            {localNotifications.length > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              background: 'linear-gradient(135deg, #1a2332 0%, #243447 100%)',
              border: '1px solid rgba(26,188,156,0.2)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              mt: 1
            }
          }
        }}
      >
        <Box
          sx={{
            width: '420px',
            maxWidth: '90vw',
            maxHeight: '600px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid rgba(26,188,156,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'rgba(0,0,0,0.2)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon sx={{ color: '#1ABC9C', fontSize: '1.2rem' }} />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.9rem'
                  }}
                >
                  Active Alerts
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '0.7rem'
                  }}
                >
                  {totalActiveEvents} active · {localNotifications.length} unread
                </Typography>
              </Box>
            </Box>

            {localNotifications.length > 0 && (
              <Tooltip title="Clear all alerts">
                <IconButton
                  size="small"
                  onClick={handleClearAll}
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    '&:hover': {
                      color: '#f44336',
                      backgroundColor: 'rgba(244,67,54,0.1)'
                    }
                  }}
                >
                  <ClearAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Alerts List */}
          {localNotifications.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                color: 'rgba(255,255,255,0.5)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <NotificationsNoneIcon sx={{ fontSize: '2.5rem', mb: 1, opacity: 0.3 }} />
              <Typography variant="body2">No active alerts</Typography>
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                All clear! ✨
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                ref={scrollContainerRef}
                sx={{
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  p: 2,
                  flex: 1,
                  '&::-webkit-scrollbar': {
                    width: '6px'
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: '3px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#1ABC9C',
                    borderRadius: '3px',
                    '&:hover': {
                      backgroundColor: '#16a085'
                    }
                  }
                }}
              >
                {localNotifications.map((notification, index) => (
                  <CompactNotificationItem
                    key={notification.id}
                    notification={notification}
                    index={index}
                    total={localNotifications.length}
                    onClose={() => handleNotificationClose(notification.id)}
                    onClick={handleNotificationClick}
                  />
                ))}
              </Box>

              {/* Footer with scroll buttons */}
              {localNotifications.length > 3 && (
                <Box
                  sx={{
                    p: 1,
                    borderTop: '1px solid rgba(26,188,156,0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    backgroundColor: 'rgba(0,0,0,0.2)'
                  }}
                >
                  <Tooltip title="Scroll to top">
                    <IconButton
                      size="small"
                      onClick={scrollToTop}
                      sx={{
                        color: '#1ABC9C',
                        '&:hover': {
                          backgroundColor: 'rgba(26,188,156,0.1)'
                        }
                      }}
                    >
                      <KeyboardArrowUpIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                  <Divider
                    orientation="vertical"
                    sx={{ my: 0.5, borderColor: 'rgba(26,188,156,0.2)' }}
                  />
                  <Tooltip title="Scroll to bottom">
                    <IconButton
                      size="small"
                      onClick={scrollToBottom}
                      sx={{
                        color: '#1ABC9C',
                        '&:hover': {
                          backgroundColor: 'rgba(26,188,156,0.1)'
                        }
                      }}
                    >
                      <KeyboardArrowDownIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationPopover;